import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Badge } from '../atoms';
import { formatDate } from '../lib/orgTimezone';
import { humanizeToken } from '../lib/inlineAttribution';

/**
 * TaxBillCollectorGroups — §4.5 (appeal-lifecycle respec) tax-bill screen.
 *
 * A parcel-year's tax bills GROUPED BY COLLECTOR (a parcel-year can hold several
 * bills, one per collector — Fulton County + Fairburn City), each collector's
 * jurisdiction lines carrying a REACHABLE base. Per line the row shows
 * jurisdiction · rate · tax; ON OPEN the base breakdown that makes the
 * math reachable — assessed → less named reduction(s) → taxable → (cap if any) →
 * tax — so a viewer can see WHY displayed rate × displayed assessed ≠ displayed
 * tax (the BEX-100003 problem: no column showed the reduction/taxable step).
 *
 * ⚠️ THIS CARD SHOWS TAX, NEVER SAVINGS — Yadira, twice (round-5 Q5, round-6 Q3):
 * *"no tax savings figure from the exemption; show the new taxes with the reduction
 * applied. No savings line, no savings in the header."* It previously derived a
 * per-line savings as `tax on the full assessed value − the actual tax` and rolled
 * that into a collector subtotal and a parcel-year header. That figure IS the
 * reduction re-expressed in tax terms, which is precisely the phantom-savings path
 * her answer forbids: a reduction changes the tax, it does not produce a saving.
 * On BEX-100003 it printed "$6,512.00 saved" against a $20,000 reduction that
 * accounts for $550 of tax. Do not reintroduce a savings figure on a tax bill.
 *
 * rate_basis (§2.10, percent vs mills) is shown so a mis-set basis (a silent 100×
 * error) is visible.
 *
 * Data: GET /api/parcel-tax-bills/:parcelId?tax_year= (enriched read). The
 * reachable base is sourced from tax_records (assessed=current_value, taxable_value,
 * exemption_amount + reduction block) so the taxable step is derived from raw
 * columns here — for a money-blind role those columns arrive NULL (redacted), so
 * every figure degrades to "—" while the collector/jurisdiction structure survives.
 */

interface BillRow {
  id: string;
  collector_id: string | null;
  collector_name: string | null;
  collector_title: string | null;
  due_date: string | null;
  status: string | null;
  statement_label: string | null;
  tax_amount: number | null;
  tax_year: number | null;
  process_year: number | null;
  bill_type: string | null;
}

interface LineRow {
  id: string;
  collector_id: string | null;
  jurisdiction_id: string | null;
  jurisdiction_name: string | null;
  jurisdiction_short: string | null;
  jurisdiction_class: string | null;
  rate_basis: string | null;               // the jurisdiction's standing declaration
  is_millage_rate: boolean | number | null; // the per-year annual flag
  // Server-resolved from the two above plus the state rule — this is what the money used.
  resolved_rate_basis: 'percent' | 'mills' | null;
  resolved_divisor: number | null;
  resolved_basis_source: 'jurisdiction' | 'jurisdiction_basis' | 'state' | 'default' | null;
  current_value: number | null; // assessed
  taxable_value: number | null;
  exemption_amount: number | null;
  reduction_current_type: string | null;
  reduction_current_status: string | null;
  reduction_current_amount: number | null;
  tax_calc_method: string | null;
  cap: number | null;
  rate: number | null;
  tax_amount: number | null;
  is_primary: boolean | number | null;
}

interface Payload {
  parcel_id: string;
  tax_year: number;
  bills: BillRow[];
  lines: LineRow[];
}

export interface TaxBillCollectorGroupsProps {
  parcelId: string;
  taxYear: number;
  /** The bill currently open on the detail page — its collector group is marked "Viewing". */
  highlightBillId?: string;
}

// Money-with-cents (tax, savings, subtotals, due) — matches the wireframe's
// "$517,851.23". A null figure (redacted / not computed) renders "—".
const money2 = (v: number | null | undefined) =>
  v == null ? '—' : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Bare value, no cents (assessed / taxable / reduction) — matches "26,435,480".
const num0 = (v: number | null | undefined) =>
  v == null ? '—' : v.toLocaleString(undefined, { maximumFractionDigits: 0 });

const round3 = (n: number) => Math.round(n * 1000) / 1000;

// The divisor the STORED tax was actually computed on, resolved by the server
// (services/rate-basis) and shipped as `resolved_rate_basis`. This component used to
// combine is_millage_rate and rate_basis itself, which made it a fifth resolver — and it
// disagreed with the money whenever a jurisdiction_annual_info row was present and false
// (the live condition on every GA/FL jurisdiction), labelling a line "% (per $100)" while
// the tax had been computed per $1,000. Display the resolved answer; do not re-derive it.
const isMillage = (l: LineRow) => l.resolved_rate_basis === 'mills';

const rateBasisLabel = (l: LineRow) => {
  const unit = isMillage(l) ? 'mills (per $1,000)' : '% (per $100)';
  // Where the answer came from, so a viewer can tell a jurisdiction's own declaration
  // from one inherited off its state — the inherited case is the one that moves if the
  // state rule is edited.
  if (l.resolved_basis_source === 'state') return `${unit} — inherited from state`;
  if (l.resolved_basis_source === 'default') return `${unit} — basis not set`;
  return unit;
};

const CAP_METHOD_LABEL: Record<string, string> = {
  capped_tax: 'Capped tax', capped_rate: 'Capped rate', capped_value: 'Capped value',
  fixed_rate: 'Fixed rate', fixed_tax: 'Fixed tax', fixed_value: 'Fixed value',
};

/** max(0, taxable − exemption) — the base the tax is actually computed on. */
function netTaxable(l: LineRow): number | null {
  if (l.taxable_value == null) return null;
  return Math.max(0, Number(l.taxable_value) - Number(l.exemption_amount ?? 0));
}
/** The value lost between assessed and taxable (a cap / circuit-breaker step). */
function capReduction(l: LineRow): number | null {
  if (l.current_value == null || l.taxable_value == null) return null;
  return Math.max(0, Number(l.current_value) - Number(l.taxable_value));
}

/** The ordered reduction steps that carry assessed → net taxable, each labeled. */
function reductionSteps(l: LineRow): Array<{ label: string; status: string | null; amount: number }> {
  const steps: Array<{ label: string; status: string | null; amount: number }> = [];
  const cap = capReduction(l);
  if (cap != null && cap > 0) {
    steps.push({
      label: (l.tax_calc_method && CAP_METHOD_LABEL[l.tax_calc_method]) || 'Assessment cap',
      status: null,
      amount: cap,
    });
  }
  const ex = Number(l.exemption_amount ?? 0);
  if (ex > 0) {
    steps.push({
      label: l.reduction_current_type || 'Exemptions',
      status: l.reduction_current_status || null,
      amount: ex,
    });
  }
  return steps;
}

/** Sum that stays null when EVERY input is null (money-blind) — else the sum of the present ones. */
function sumOrNull(values: Array<number | null>): number | null {
  if (values.every((v) => v == null)) return null;
  return round3(values.reduce<number>((a, v) => a + (v ?? 0), 0));
}

const billStatusVariant: Record<string, 'success' | 'warning' | 'neutral' | 'info' | 'danger'> = {
  no_activity: 'neutral', received: 'warning', processed: 'info',
  approved: 'success', transmitted: 'info', paid: 'success', void: 'danger',
};

interface CollectorGroup {
  collectorId: string;
  collectorName: string;
  bills: BillRow[];
  lines: LineRow[];
  dueDate: string | null;
  subtotalTax: number | null;
}

export function TaxBillCollectorGroups({ parcelId, taxYear, highlightBillId }: TaxBillCollectorGroupsProps) {
  const [openLines, setOpenLines] = useState<Set<string>>(new Set());
  const toggleLine = (id: string) =>
    setOpenLines((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const query = useQuery<Payload>({
    queryKey: ['parcel-tax-bills', parcelId, taxYear],
    queryFn: ({ signal }) => api.get<Payload>(`/api/parcel-tax-bills/${parcelId}?tax_year=${taxYear}`, signal),
    enabled: !!parcelId && taxYear != null,
  });

  const payload = query.data && !Array.isArray(query.data) ? query.data : null;
  const bills = payload?.bills ?? [];
  const lines = payload?.lines ?? [];

  const { groups, totalDue } = useMemo(() => {
    const byCollector = new Map<string, CollectorGroup>();
    const ensure = (cid: string | null, name: string | null): CollectorGroup => {
      const key = cid ?? '__none__';
      let g = byCollector.get(key);
      if (!g) {
        g = { collectorId: key, collectorName: name || 'Unassigned collector', bills: [], lines: [], dueDate: null, subtotalTax: null };
        byCollector.set(key, g);
      } else if (name && g.collectorName === 'Unassigned collector') {
        g.collectorName = name;
      }
      return g;
    };
    for (const b of bills) ensure(b.collector_id, b.collector_name).bills.push(b);
    for (const l of lines) ensure(l.collector_id, null).lines.push(l);

    const groupsArr = [...byCollector.values()].map((g) => {
      g.dueDate = g.bills.find((b) => b.due_date)?.due_date ?? null;
      // Subtotals are LINE-derived (correct even in the multi-collector case, where
      // each bill.tax_amount currently carries the whole parcel-year total). Fall
      // back to the bill total only when a collector has a bill but no lines.
      g.subtotalTax = g.lines.length
        ? sumOrNull(g.lines.map((l) => l.tax_amount))
        : sumOrNull(g.bills.map((b) => b.tax_amount));
      return g;
    });
    // Named collectors first (a group is "named" when it has a real collector id).
    groupsArr.sort((a, b) => a.collectorName.localeCompare(b.collectorName));

    const totalDue = sumOrNull(groupsArr.map((g) => g.subtotalTax));
    return { groups: groupsArr, totalDue };
  }, [bills, lines]);

  // Unobtrusive: while loading OR when the parcel-year has nothing, render nothing —
  // the bill detail above already stands on its own.
  if (query.isLoading || (groups.length === 0)) return null;

  return (
    <section
      aria-label="Tax bills for this parcel by collector"
      style={{
        border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)',
        background: 'var(--color-card)', padding: 'var(--sp-4) var(--sp-5)', margin: 'var(--sp-4) 0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--sp-3)', flexWrap: 'wrap', marginBottom: 'var(--sp-3)' }}>
        <div style={{
          fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)', color: 'var(--color-muted)',
        }}>
          Tax bills · {taxYear} · by collector
        </div>
        {/* A tax bill states TAX, never savings — see the note at the top of this file. */}
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
          <strong style={{ color: 'var(--color-ink)' }}>{money2(totalDue)}</strong> due
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        {groups.map((g) => {
          const isViewing = !!highlightBillId && g.bills.some((b) => b.id === highlightBillId);
          const billStatus = g.bills[0]?.status ?? null;
          return (
            <div key={g.collectorId} style={{
              border: `1px solid ${isViewing ? 'var(--brand-primary)' : 'var(--color-border)'}`,
              borderRadius: 'var(--r-md)', overflow: 'hidden',
            }}>
              {/* Collector header: name · due date · per-collector subtotal (wireframe). */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)',
                flexWrap: 'wrap', padding: 'var(--sp-3) var(--sp-4)', background: 'var(--color-bg-subtle, var(--color-card))',
                borderBottom: '1px solid var(--color-border)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{g.collectorName}</span>
                  {g.dueDate && <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>due {formatDate(g.dueDate)}</span>}
                  {billStatus && <Badge variant={billStatusVariant[billStatus] ?? 'neutral'} dot>{billStatus.replace(/_/g, ' ')}</Badge>}
                  {isViewing && <Badge variant="info">Viewing</Badge>}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-3)' }}>
                  <span style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{money2(g.subtotalTax)}</span>
                </div>
              </div>

              {/* Jurisdiction lines: row = jurisdiction · rate · tax; open = reachable base. A tax
                  bill states TAX, never savings — see the header note (round 6, Q3). */}
              {g.lines.length === 0 ? (
                <div style={{ padding: 'var(--sp-3) var(--sp-4)', fontSize: 'var(--fs-sm)', color: 'var(--color-muted)' }}>
                  No jurisdiction lines recorded for this bill.
                </div>
              ) : (
                <div>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1.6fr 1.2fr 1fr auto', gap: 'var(--sp-2)',
                    padding: 'var(--sp-2) var(--sp-4)', fontSize: 'var(--fs-xs)', color: 'var(--color-muted)',
                    textTransform: 'uppercase', letterSpacing: 'var(--ls-wide)', borderBottom: '1px solid var(--color-border)',
                  }}>
                    <span>Jurisdiction</span>
                    <span style={{ textAlign: 'right' }}>Rate</span>
                    <span style={{ textAlign: 'right' }}>Tax</span>
                    <span />
                  </div>
                  {g.lines.map((l) => {
                    const open = openLines.has(l.id);
                    const steps = reductionSteps(l);
                    const net = netTaxable(l);
                    return (
                      <div key={l.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <button
                          type="button"
                          onClick={() => toggleLine(l.id)}
                          aria-expanded={open}
                          style={{
                            width: '100%', display: 'grid', gridTemplateColumns: '1.6fr 1.2fr 1fr auto',
                            gap: 'var(--sp-2)', alignItems: 'center', padding: 'var(--sp-2) var(--sp-4)',
                            background: 'none', border: 'none', cursor: 'pointer', font: 'inherit',
                            textAlign: 'left', color: 'var(--color-ink)',
                          }}
                        >
                          <span style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 'var(--fw-medium)' }}>{l.jurisdiction_name || l.jurisdiction_short || '—'}</span>
                            {l.jurisdiction_class && (
                              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{l.jurisdiction_class}</span>
                            )}
                          </span>
                          <span style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)' }}>
                            {l.rate != null ? l.rate : '—'}
                            <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', fontFamily: 'inherit' }}>
                              {/* The RESOLVED unit, not the raw column — a jurisdiction
                                  with no standing basis still bills on a real divisor,
                                  and this sits directly under the rate it qualifies. */}
                              {isMillage(l) ? 'mills' : '%'}
                            </span>
                          </span>
                          <span style={{ textAlign: 'right', fontWeight: 'var(--fw-medium)' }}>{money2(l.tax_amount)}</span>
                          <span aria-hidden style={{ color: 'var(--color-ink-3)', paddingLeft: 'var(--sp-2)' }}>{open ? '▾' : '▸'}</span>
                        </button>

                        {open && (
                          <div style={{
                            padding: 'var(--sp-3) var(--sp-4)', margin: '0 var(--sp-3) var(--sp-3)',
                            background: 'var(--color-bg-subtle, var(--color-card))', border: '1px solid var(--color-border)',
                            borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)',
                          }}>
                            {/* The reachable base: assessed → less reduction(s) → taxable → tax. */}
                            <BaseRow label="Assessed" value={num0(l.current_value)} />
                            {steps.map((s, i) => (
                              <BaseRow
                                key={i}
                                // §6 — underscores out; lowercase back in, because this
                                // is a parenthetical inside running text ("Less Abatement
                                // (granted)"), not a standalone status pill.
                                label={`Less ${s.label}${s.status ? ` (${humanizeToken(s.status).toLowerCase()})` : ''}`}
                                value={`− ${num0(s.amount)}`}
                                muted
                              />
                            ))}
                            {steps.length === 0 && (
                              <BaseRow label="No reduction on this line" value="" muted />
                            )}
                            <BaseRow label="Taxable" value={num0(net)} strong />
                            {l.tax_calc_method && l.tax_calc_method !== 'standard' && (
                              <BaseRow
                                label={`Cap method — ${CAP_METHOD_LABEL[l.tax_calc_method] || l.tax_calc_method}`}
                                value={l.cap != null ? money2(l.cap) : '—'}
                                muted
                              />
                            )}
                            <BaseRow label={`Tax (× rate ${l.rate ?? '—'}, ${rateBasisLabel(l)})`} value={money2(l.tax_amount)} strong />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function BaseRow({ label, value, strong, muted }: { label: string; value: string; strong?: boolean; muted?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)', padding: 'var(--sp-1) 0',
      borderBottom: '1px solid var(--color-border)',
    }}>
      <span style={{ color: muted ? 'var(--color-ink-3)' : 'var(--color-ink-2)' }}>{label}</span>
      <span style={{
        fontWeight: strong ? 'var(--fw-semibold)' : 'var(--fw-regular)',
        color: strong ? 'var(--color-ink)' : 'var(--color-ink-2)',
        fontFamily: 'var(--font-mono)', textAlign: 'right',
      }}>{value}</span>
    </div>
  );
}
